import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { costCenterLabel as ccLabel, PERSON_PHOTO_SIDE } from '@loot/shared';
import { api } from '../../api.ts';
import { Badge } from '../../components/ui.tsx';

/**
 * Situação funcional que as telas mostram (vínculo + afastamento ou licença vigente). Sem nenhuma
 * movimentação registrada, é "não informada" (nunca desligado, nem ativo por suposição).
 */
export type FunctionalStatus =
  'not_informed' | 'active' | 'absent' | 'on_leave' | 'terminated' | 'ended';

/** Situação cadastral: em preenchimento, completo ou inativo (separada da situação funcional). */
export type RegistrationStatus = 'in_progress' | 'complete' | 'inactive';

export interface Completeness {
  level: 'complete' | 'partial' | 'pending';
  percent: number;
  missing: string[];
}

export interface Named {
  id: string;
  code: string;
  name: string;
}

export interface PeopleOptions {
  legalEntities: Named[];
  branches: (Named & { legalEntityId: string })[];
  departments: (Named & { legalEntityId: string | null; costCenterId: string | null })[];
  costCenters: (Named & {
    legalEntityId: string;
    departmentId: string | null;
    parentId: string | null;
    structureLevel: string | null;
    isActive: boolean;
    validFrom: string;
    validUntil: string | null;
  })[];
  locations: (Named & { legalEntityId: string; branchId: string })[];
  jobs: (Named & {
    marketName: string | null;
    levelCode: string | null;
    /** Inativo só vem para o master, que localiza todos os cargos. */
    status: string;
    familyId: string | null;
    familyName: string | null;
    subfamilyName: string | null;
    /** Trilha Liderança: lidera famílias e atua em qualquer subfamília (ADR 0054). */
    leadership: boolean;
  })[];
  /** Catálogo ativo, para a atuação dos cargos de liderança e para a liderança (ADR 0054). */
  families: { id: string; code: string; name: string }[];
  subfamilies: { id: string; code: string; name: string; familyId: string }[];
  /** Famílias, subfamílias e exceções por cargo de cada departamento (ADR 0096). */
  departmentJobScopes: {
    departmentId: string;
    familyId: string | null;
    subfamilyId: string | null;
    jobId: string | null;
  }[];
  /** Subfamílias que cada cargo aceita na movimentação: a principal e as de atuação (ADR 0044). */
  jobSubfamilies: {
    jobId: string;
    subfamilyId: string;
    name: string;
    marketName: string | null;
    principal: boolean;
  }[];
  employmentTypes: {
    id: string;
    code: string;
    name: { 'pt-BR': string; en: string };
    hasRegistration: boolean;
    hasWorkday: boolean;
    isActive: boolean;
  }[];
  managers: { id: string; name: string; jobName: string | null; legalEntityCode: string | null }[];
  users: { id: string; name: string; email: string }[];
}

/** Linha de rateio como a API devolve: a empresa beneficiária é a do centro de custo. */
export interface AllocationLineView {
  id?: string;
  costCenterId: string;
  costCenterCode: string | null;
  costCenterName: string | null;
  beneficiaryLegalEntityId?: string | null;
  beneficiaryLegalEntityCode?: string | null;
  beneficiaryLegalEntityName?: string | null;
  percentage: string;
}

/** Percentual para leitura (até 4 casas). */
export function pct(value: string | number): string {
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Centro de custo como "EMPRESA · código - nome" (rótulo de costCenterLabel em @loot/shared). */
export function costCenterLabel(
  cc: { code: string; name: string; legalEntityId: string },
  options: PeopleOptions,
): string {
  const entity = options.legalEntities.find((e) => e.id === cc.legalEntityId)?.code ?? '?';
  return `${entity} · ${ccLabel(cc.code, cc.name)}`;
}

/** Soma dos percentuais por empresa beneficiária (rateio entre empresas do grupo). */
export function totalsByBeneficiary(
  lines: AllocationLineView[],
): { code: string; total: number }[] {
  const totals = new Map<string, number>();
  for (const l of lines) {
    const code = l.beneficiaryLegalEntityCode ?? '?';
    totals.set(code, (totals.get(code) ?? 0) + Number(l.percentage));
  }
  return [...totals].map(([code, total]) => ({ code, total: Math.round(total * 10000) / 10000 }));
}

export function usePeopleOptions(enabled = true) {
  return useQuery({
    queryKey: ['people', 'options'],
    queryFn: () => api.get<PeopleOptions>('/people/options'),
    staleTime: 30_000,
    enabled,
  });
}

export interface CareerCatalog {
  families: {
    id: string;
    code: string;
    name: string;
    shortCode: string | null;
    description: string | null;
    isActive: boolean;
  }[];
  subfamilies: {
    id: string;
    familyId: string;
    code: string;
    name: string;
    kind: string | null;
    description: string | null;
    isActive: boolean;
  }[];
  levels: {
    id: string;
    code: string;
    name: string;
    rank: number;
    description: string | null;
    isActive: boolean;
  }[];
  tracks: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    isActive: boolean;
  }[];
  grades: {
    id: string;
    code: string;
    familyId: string | null;
    levelId: string | null;
    description: string | null;
    isActive: boolean;
  }[];
  currencies: { code: string }[];
  actions: { manage: boolean; salaryRead: boolean; salaryManage: boolean };
}

export function useCareerCatalog() {
  return useQuery({
    queryKey: ['career', 'catalog'],
    queryFn: () => api.get<CareerCatalog>('/career/catalog'),
    staleTime: 30_000,
  });
}

export function StatusBadge({ status }: { status: FunctionalStatus | null }) {
  const { t } = useTranslation();
  const s = status ?? 'not_informed';
  const tone =
    s === 'active'
      ? 'ok'
      : s === 'on_leave' || s === 'absent'
        ? 'info'
        : s === 'not_informed'
          ? 'accent'
          : 'off';
  return <Badge tone={tone}>{t(`people.status.${s}`)}</Badge>;
}

export function RegistrationBadge({ status }: { status: RegistrationStatus }) {
  const { t } = useTranslation();
  const tone = status === 'complete' ? 'ok' : status === 'inactive' ? 'off' : 'info';
  return <Badge tone={tone}>{t(`people.registration.${status}`)}</Badge>;
}

/** Completude: completo, parcial ou pendente, com o percentual e o que falta (sem valores). */
export function CompletenessBadge({
  c,
  detailed = false,
}: {
  c: Completeness;
  detailed?: boolean;
}) {
  const { t } = useTranslation();
  const tone = c.level === 'complete' ? 'ok' : c.level === 'partial' ? 'info' : 'off';
  return (
    <span className="flex flex-col gap-1">
      <span>
        <Badge tone={tone}>
          {t(`people.completeness.${c.level}`)} · {c.percent}%
        </Badge>
      </span>
      {detailed && c.missing.length > 0 && (
        <span className="text-xs text-muted">
          {t('people.completeness.missing')}:{' '}
          {c.missing.map((m) => t(`people.completeness.fields.${m}`)).join(', ')}
        </span>
      )}
    </span>
  );
}

/** Abas (o mesmo visual da ficha de parceiros). */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: readonly T[];
  value: T;
  onChange: (t: T) => void;
  label: (t: T) => string;
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1 border-b border-rule">
      {tabs.map((k) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={value === k}
          onClick={() => onChange(k)}
          className={
            value === k
              ? '-mb-px border-b-2 border-accent px-3 py-2 text-sm font-medium text-ink'
              : 'px-3 py-2 text-sm text-muted hover:text-ink'
          }
        >
          {label(k)}
        </button>
      ))}
    </div>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm break-words">{children || '—'}</dd>
    </div>
  );
}

export function Facts({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</dl>;
}

/** Estado de formulário simples (um objeto de textos). */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [form, setForm] = useState<T>(initial);
  const set = (patch: Partial<T>) => setForm((f) => ({ ...f, ...patch }));
  return { form, set, reset: setForm };
}

/** Converte texto vazio em null (o que a API espera para campos não informados). */
export function blankToNull<T extends Record<string, unknown>>(v: T): T {
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x === '' ? null : x])) as T;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Nome do tipo de vínculo no idioma da tela. */
export function useLocalized() {
  const { i18n } = useTranslation();
  return (text: { 'pt-BR': string; en: string } | null | undefined) =>
    text ? (i18n.language === 'en' ? text.en : text['pt-BR']) : '—';
}

/** Iniciais do nome (até duas), para quem ainda não tem foto. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Foto da pessoa (ADR 0069) num círculo; sem foto, as iniciais do nome. */
export function PersonAvatar({
  name,
  photoId,
  size = 'sm',
}: {
  name: string;
  photoId: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const box =
    size === 'lg' ? 'h-24 w-24 text-2xl' : size === 'md' ? 'h-12 w-12 text-sm' : 'h-8 w-8 text-xs';
  return photoId ? (
    <img
      src={`/api/people/photos/${photoId}`}
      alt={name}
      loading="lazy"
      className={`${box} shrink-0 rounded-full border border-rule object-cover`}
    />
  ) : (
    <span
      aria-hidden="true"
      className={`${box} inline-flex shrink-0 items-center justify-center rounded-full border border-rule bg-paper font-medium text-muted`}
    >
      {initials(name)}
    </span>
  );
}

/**
 * Reduz a foto no navegador antes de enviar (lado maior com PERSON_PHOTO_SIDE pixels, JPEG), para o
 * arquivo ficar pequeno e o organograma carregar rápido.
 */
export async function resizePhoto(file: File): Promise<Blob> {
  const image = await createImageBitmap(file);
  const scale = Math.min(1, PERSON_PHOTO_SIDE / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.close();
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob ?? file), 'image/jpeg', 0.85),
  );
}
